package xim.poc.game.configuration.v0.tactics

import xim.poc.game.AugmentId
import xim.poc.game.CombatBonusAggregate
import xim.poc.game.GameEngine.displayName
import xim.poc.game.StatusEffect
import xim.poc.game.configuration.WeightedTable
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.TacticsInstinct
import xim.poc.game.configuration.v0.TacticsInstinctId
import xim.resource.table.AugmentTable
import xim.util.RandHelper
import kotlin.random.Random
import kotlin.random.nextInt

class TacticsInstinctDefinition(
    val id: TacticsInstinctId,
    val rarity: StatRank,
    val rarityWeight: Double = 1.0,
    val minimumEnemyRank: StatRank,
    val potencyProvider: (StatRank) -> Int,
    val displayTextProvider: (TacticsInstinct) -> String,
    val toolTipProvider: (TacticsInstinct) -> String,
    val skillUnlock: SkillId? = null,
    val enablePass: Boolean = false,
    val movement: Int? = null,
    val playerAvailable: Boolean = true,
)

object TacticsInstincts {

    private val instincts = HashMap<TacticsInstinctId, TacticsInstinctDefinition>()

    val magicAttackBonus = register(TacticsInstinctDefinition(
        id = 1,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { it.offset * 4 + r(4) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.MagicAttackBonus, it.potency) },
        toolTipProvider = { "Increases damage from magical attacks and all spells by ${it.potency}%" },
    ))

    val attackBonus = register(TacticsInstinctDefinition(
        id = 2,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { it.offset * 4 + r(4) },
        displayTextProvider = { "\"Phys. Atk. Bns\" +${it.potency}" },
        toolTipProvider = { "Increases damage from physical attacks by ${it.potency}%" },
    ))

    val evasion = register(TacticsInstinctDefinition(
        id = 3,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { it.offset * 3 + r(3) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.Evasion, it.potency) },
        toolTipProvider = { "Increases change to evade physical attacks by ${it.potency}%" },
    ))

    val whiteMagicRange = register(TacticsInstinctDefinition(
        id = 4,
        rarity = StatRank.B,
        minimumEnemyRank = StatRank.C,
        potencyProvider = { 1 },
        displayTextProvider = { "White Magic: Target range +${it.potency}" },
        toolTipProvider = { "Increases target-range of White Magic spells by ${it.potency}" },
    ))

    val blackMagicRange = register(TacticsInstinctDefinition(
        id = 5,
        rarity = StatRank.B,
        minimumEnemyRank = StatRank.C,
        potencyProvider = { 1 },
        displayTextProvider = { "Black Magic: Target range +${it.potency}" },
        toolTipProvider = { "Increases target-range of Black Magic spells by ${it.potency}" },
    ))

    val pdt = register(TacticsInstinctDefinition(
        id = 6,
        rarity = StatRank.D,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { -it.offset * 3 - r(3) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.PhysicalDamageTaken, it.potency) },
        toolTipProvider = { "Reduces physical damage taken by ${it.potency * -1}%" },
    ))

    val mdt = register(TacticsInstinctDefinition(
        id = 7,
        rarity = StatRank.D,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { -it.offset * 3 - r(3) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.MagicalDamageTaken, it.potency) },
        toolTipProvider = { "Reduces magical damage taken by ${it.potency * -1}%" },
    ))

    val dt = register(TacticsInstinctDefinition(
        id = 8,
        rarity = StatRank.C,
        minimumEnemyRank = StatRank.C,
        potencyProvider = { -it.offset * 2 - r(2) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.DamageTaken, it.potency) },
        toolTipProvider = { "Reduces all damage taken by ${it.potency * -1}%" },
    ))

    // Ancient magic: [10, 21]

    val enmity = register(TacticsInstinctDefinition(
        id = 22,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { RandHelper.signInt() * (it.offset * 10 + r(10)) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.Enmity, it.potency) },
        toolTipProvider = { "Modifies enemies' desire to target this unit by ${it.potency}%" },
    ))

    val pass = register(TacticsInstinctDefinition(
        id = 23,
        rarity = StatRank.B,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { 1 },
        displayTextProvider = { "Enables \"Pass\"" },
        toolTipProvider = { "Allows movement through enemy units" },
        enablePass = true,
    ))

    val move = register(TacticsInstinctDefinition(
        id = 24,
        rarity = StatRank.B,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { 1 },
        displayTextProvider = { "Move +1" },
        toolTipProvider = { "Increases movement range by ${it.potency}. (Max: 3)" },
        movement = 1,
    ))

    val subtleBlow = register(TacticsInstinctDefinition(
        id = 25,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { it.offset * 10 + r(10) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.SubtleBlow, it.potency) },
        toolTipProvider = { "Decreases TP gained by enemies when attacked by ${it.potency}%" },
    ))

    val curePotency = register(TacticsInstinctDefinition(
        id = 26,
        rarity = StatRank.D,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { it.offset * 4 + r(4) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.CurePotency, it.potency) },
        toolTipProvider = { "Increases the potency of healing abilities and spells by ${it.potency}%" },
    ))

    val regen = register(TacticsInstinctDefinition(
        id = 27,
        rarity = StatRank.D,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { it.offset + r(1) },
        displayTextProvider = { "Regen +${it.potency}%" },
        toolTipProvider = { "Gain ${it.potency}% of Max HP at start of unit's turn." },
    ))

    val haste = register(TacticsInstinctDefinition(
        id = 28,
        rarity = StatRank.C,
        minimumEnemyRank = StatRank.C,
        potencyProvider = { 3 + it.offset + r(3) },
        displayTextProvider = { "Haste +${it.potency}%" },
        toolTipProvider = { "Reduces TP cost of abilities and spells by ${it.potency}%" },
    ))

    val ignoresEnmity = register(TacticsInstinctDefinition(
        id = 29,
        rarity = StatRank.D,
        minimumEnemyRank = StatRank.D,
        potencyProvider = { 0 },
        displayTextProvider = { "Ignores \"Enmity\"" },
        toolTipProvider = { "Ignores the effect of Enmity when scoring possible actions" },
        playerAvailable = false,
    ))

    val magicRange = register(TacticsInstinctDefinition(
        id = 30,
        rarity = StatRank.A,
        minimumEnemyRank = StatRank.B,
        potencyProvider = { 1 },
        displayTextProvider = { "White/Black Magic: Target range +${it.potency}" },
        toolTipProvider = { "Increases target-range of White Magic and Black Magic spells by ${it.potency}" },
    ))

    val maxHp = register(TacticsInstinctDefinition(
        id = 31,
        rarity = StatRank.A,
        minimumEnemyRank = StatRank.B,
        potencyProvider = { it.offset * 3 + r(3) },
        displayTextProvider = { "Max HP +${it.potency}%" },
        toolTipProvider = { "Increases Max HP by ${it.potency}%" },
    ))

    val spontaneity = register(TacticsInstinctDefinition(
        id = 32,
        rarity = StatRank.A,
        minimumEnemyRank = StatRank.B,
        potencyProvider = { it.offset * 5 + r(10) },
        displayTextProvider = { "Occ. grants \"Spontaneity\" +${it.potency}%" },
        toolTipProvider = { "+${it.potency}% chance of gaining 1 charge of \"Spontaneity\" when using an offensive ability." },
    ))

    val resistSilence = register(TacticsInstinctDefinition(
        id = 33,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.E,
        potencyProvider = { 0 },
        displayTextProvider = { "\"Resist Silence\" +${it.potency}%" },
        toolTipProvider = { "Increases the chance of resisting \"Silence\" by ${it.potency}%" },
        playerAvailable = false
    ))

    val criticalHitDamage = register(TacticsInstinctDefinition(
        id = 34,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.C,
        potencyProvider = { it.offset * 4 + r(4) },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.CriticalHitDamage, it.potency) },
        toolTipProvider = { "Increases critical hit damage by ${it.potency}%" },
    ))

    val fullCure = register(TacticsInstinctDefinition(
        id = 10 + 35,
        rarity = StatRank.B,
        minimumEnemyRank = StatRank.C,
        potencyProvider = { 1 },
        displayTextProvider = { "Able to cast \"${spellFullCure_893.displayName()}\"" },
        toolTipProvider = { TacticsSkills[spellFullCure_893].description },
        skillUnlock = spellFullCure_893
    ))

    val resistAll = register(TacticsInstinctDefinition(
        id = 35,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.E,
        potencyProvider = { 0 },
        displayTextProvider = { AugmentTable.getAugmentName(AugmentId.OccAllStatusResist, it.potency) },
        toolTipProvider = { "Increases the chance of resisting all debuffs by ${it.potency}%" },
        playerAvailable = false
    ))

    val resistParalysis = register(TacticsInstinctDefinition(
        id = 36,
        rarity = StatRank.E,
        minimumEnemyRank = StatRank.E,
        potencyProvider = { 0 },
        displayTextProvider = { "\"Resist Paralysis\" +${it.potency}%" },
        toolTipProvider = { "Increases the chance of resisting \"Paralysis\" by ${it.potency}%" },
        playerAvailable = false
    ))

    init {
        registerAncientMagicInstincts()
    }

    operator fun get(id: TacticsInstinctId): TacticsInstinctDefinition {
        return instincts[id] ?: throw IllegalStateException("No such instinct: $id")
    }

    fun getRandom(unitPower: StatRank, count: Int, weightModifier: (Double) -> Double = { it }): List<TacticsInstinct> {
        val validInstincts = instincts.values.filter { it.minimumEnemyRank.value <= unitPower.value }
            .filter { it.playerAvailable }
            .groupBy { it.rarity }

        val weightedTable = WeightedTable(validInstincts.entries.map { it.value to weightModifier.invoke(rankToRarity(it.key)) })

        val chosen = ArrayList<TacticsInstinct>()
        for (i in 0 until count) {
            val nextGroup = weightedTable.getRandom().filter { chosen.none { c -> c.id == it.id } }
            if (nextGroup.isEmpty()) { continue }

            val rarityTable = WeightedTable(nextGroup.map { it to it.rarityWeight })
            val next = rarityTable.getRandom()

            chosen += TacticsInstinct(next.id, next.potencyProvider(unitPower))
        }

        return chosen
    }

    fun apply(instincts: List<TacticsInstinct>, aggregate: CombatBonusAggregate) {
        instincts.forEach { apply(it, aggregate) }
    }

    fun apply(instinct: TacticsInstinct, aggregate: CombatBonusAggregate) {
        when (instinct.id) {
            magicAttackBonus.id -> aggregate.magicAttackBonus += instinct.potency
            attackBonus.id -> aggregate.physicalDamageBonus += instinct.potency
            evasion.id -> aggregate.evasionRate += instinct.potency
            pdt.id, dt.id -> aggregate.physicalDamageTaken += instinct.potency
            mdt.id, dt.id -> aggregate.magicalDamageTaken += instinct.potency
            enmity.id -> aggregate.enmity += instinct.potency
            subtleBlow.id -> aggregate.subtleBlow += instinct.potency
            curePotency.id -> aggregate.curePotency += instinct.potency
            regen.id -> aggregate.regen += (aggregate.actorState.getMaxHp() * instinct.potency)/100
            haste.id -> aggregate.haste += instinct.potency
            resistSilence.id -> aggregate.resist(StatusEffect.Silence, instinct.potency)
            criticalHitDamage.id -> aggregate.criticalHitDamage += instinct.potency
            resistAll.id -> aggregate.allStatusResistRate += instinct.potency
            resistParalysis.id -> aggregate.resist(StatusEffect.Paralysis, instinct.potency)
        }
    }

    private fun register(def: TacticsInstinctDefinition): TacticsInstinctDefinition {
        if (instincts.containsKey(def.id)) { throw IllegalStateException("Duplicate key: ${def.id}") }
        instincts[def.id] = def
        return def
    }

    private fun rankToRarity(statRank: StatRank): Double {
        return when (statRank) {
            StatRank.F -> 0.000
            StatRank.E -> 0.415
            StatRank.D -> 0.250
            StatRank.C -> 0.150
            StatRank.B -> 0.100
            StatRank.A -> 0.050
            StatRank.S -> 0.025
            StatRank.X -> 0.010
        }
    }

    private fun r(range: Int): Int {
        return Random.nextInt(0 .. range)
    }

    private fun registerAncientMagicInstincts() {
        val spells = (spellFlare_204.id .. spellFloodII_215.id).map { SpellSkillId(it) }

        for (i in spells.indices) {
            val spell = spells[i]
            val tier = spell.displayName().split(" ").size

            if (tier == 1) {
                register(TacticsInstinctDefinition(
                    id = 10 + i,
                    rarity = StatRank.B,
                    rarityWeight = 1.0/6.0,
                    minimumEnemyRank = StatRank.C,
                    potencyProvider = { 1 },
                    displayTextProvider = { "Able to cast \"${spell.displayName()}\"" },
                    toolTipProvider = { TacticsSkills[spell].description },
                    skillUnlock = spell
                ))
            } else {
                register(TacticsInstinctDefinition(
                    id = 10 + i,
                    rarity = StatRank.A,
                    rarityWeight = 1.0/6.0,
                    minimumEnemyRank = StatRank.A,
                    potencyProvider = { 1 },
                    displayTextProvider = { "Able to cast \"${spell.displayName()}\"" },
                    toolTipProvider = { TacticsSkills[spell].description },
                    skillUnlock = spell
                ))
            }
        }
    }

}

fun TacticsInstinct.display(): String {
    return TacticsInstincts[this.id].displayTextProvider.invoke(this)
}

fun TacticsInstinct.toolTip(): String {
    return TacticsInstincts[this.id].toolTipProvider.invoke(this)
}